// Equipment catalogue, save game and the money verbs.
//
// Every function here is pure: it takes a SaveGame and returns a TxResult
// holding a NEW SaveGame (deep-cloned); the input is never mutated. On a
// refused transaction `ok` is false, `reason` is player-facing copy and
// `save` is the unchanged input.

import type { EquipmentModel, JobResult, OwnedEquipment, ToolKind } from './types';
import type { StaffState } from './staff';
import type { VisitRecord } from './revisits';
import { mixSeed, mulberry32 } from './rng';

export const MODELS: Record<string, EquipmentModel> = {
  'redgum-46': {
    key: 'redgum-46', name: 'Redgum 46', brand: 'Redgum', kind: 'mower', priceAud: 499,
    deckCm: 46, speedKmh: 4, tolerance: 1.0, power: 'petrol', burnLph: 0.9, tankL: 1.2, style: 'push', dischargeSide: 'right', tier: 1,
    blurb: 'Came with the business. Starts third pull, most days.',
  },
  'redgum-46sp': {
    key: 'redgum-46sp', name: 'Redgum 46 SP', brand: 'Redgum', kind: 'mower', priceAud: 899,
    deckCm: 46, speedKmh: 4.5, tolerance: 1.05, power: 'petrol', burnLph: 0.9, tankL: 1.3, style: 'push', selfPropelled: true, dischargeSide: 'right', tier: 1,
    blurb: 'Same deck, but it walks itself. Thumb paddle, not shoulder.',
  },
  'redgum-53sp': {
    key: 'redgum-53sp', name: 'Redgum 53 SP', brand: 'Redgum', kind: 'mower', priceAud: 1299,
    deckCm: 53, speedKmh: 5, tolerance: 1.1, power: 'petrol', burnLph: 0.9, tankL: 1.5, style: 'push', selfPropelled: true, dischargeSide: 'right', tier: 2,
    blurb: 'Self-propelled. Your lower back sends its regards.',
  },
  'redgum-rider-96': {
    key: 'redgum-rider-96', name: 'Redgum Rider 96', brand: 'Redgum', kind: 'mower', priceAud: 6900,
    deckCm: 96, speedKmh: 9, tolerance: 1.4, power: 'petrol', burnLph: 2.5, tankL: 6, rideOn: true, style: 'ride-on', dischargeSide: 'right', tier: 3,
    blurb: 'A seat, a cup holder and a 96 cm deck. Living the dream.',
  },
  'jarrah-zt-106': {
    key: 'jarrah-zt-106', name: 'Jarrah Iron ZT-106', brand: 'Jarrah Iron', kind: 'mower', priceAud: 9999,
    deckCm: 106, speedKmh: 12, tolerance: 1.6, power: 'petrol', burnLph: 2.2, tankL: 9, rideOn: true, dischargeSide: 'right', style: 'zero-turn', tier: 4,
    blurb: 'Zero-turn on two levers. Spins on the spot, throws it out the side.',
  },
  'jarrah-zt-137': {
    key: 'jarrah-zt-137', name: 'Jarrah Iron ZT-137', brand: 'Jarrah Iron', kind: 'mower', priceAud: 21500,
    deckCm: 137, speedKmh: 14, tolerance: 1.8, power: 'petrol', burnLph: 2.5, tankL: 12, rideOn: true, collector: true, dischargeSide: 'none', style: 'zero-turn', tier: 5,
    blurb: 'Zero-turn, catcher on the back. Council-grade grunt.',
  },
  'coolabah-s100': {
    key: 'coolabah-s100', name: 'Coolabah S100', brand: 'Coolabah', kind: 'trimmer', priceAud: 109,
    deckCm: 25, power: 'battery', runS: 60, tier: 1,
    blurb: 'Small pack, thin line. Does a front yard, then wants the charger.',
  },
  'coolabah-s300': {
    key: 'coolabah-s300', name: 'Coolabah S300', brand: 'Coolabah', kind: 'trimmer', priceAud: 349,
    deckCm: 30, power: 'battery', tier: 1,
    blurb: 'Battery whipper snipper. Quiet enough for a Sunday.',
  },
  'coolabah-s600': {
    key: 'coolabah-s600', name: 'Coolabah S600', brand: 'Coolabah', kind: 'trimmer', priceAud: 599,
    deckCm: 40, power: 'battery', runS: 240, tier: 3,
    blurb: 'Big pack, thick line. Twice the run of the S300 on a charge.',
  },
  'redgum-snip-26': {
    key: 'redgum-snip-26', name: 'Redgum Snip 26', brand: 'Redgum', kind: 'trimmer', priceAud: 279,
    deckCm: 26, power: 'petrol', burnLph: 0.4, tankL: 0.5, tier: 1,
    blurb: 'Two-stroke snipper. Smells like every Saturday of your childhood.',
  },
  'coolabah-b200': {
    key: 'coolabah-b200', name: 'Coolabah B200', brand: 'Coolabah', kind: 'blower', priceAud: 99,
    deckCm: 160, power: 'battery', runS: 60, tier: 1,
    blurb: 'Handheld puffer. Fine for a doorstep, not a driveway.',
  },
  'coolabah-b600': {
    key: 'coolabah-b600', name: 'Coolabah B600', brand: 'Coolabah', kind: 'blower', priceAud: 499,
    deckCm: 250, power: 'battery', tier: 2,
    blurb: 'Battery blower. Moves leaves, not the neighbours.',
  },
  'redgum-gale-65': {
    key: 'redgum-gale-65', name: 'Redgum Gale 65', brand: 'Redgum', kind: 'blower', priceAud: 749,
    deckCm: 400, power: 'petrol', burnLph: 0.6, tankL: 1.5, tier: 3,
    blurb: 'Backpack blower. Shifts bark off an oval in one go.',
  },
  'coolabah-v450': {
    key: 'coolabah-v450', name: 'Coolabah V450', brand: 'Coolabah', kind: 'blower', priceAud: 499,
    deckCm: 100, power: 'battery', runS: 90, collector: true, tier: 2,
    blurb: 'Garden vac. Sucks the leaves up and bags them. No tarp, short reach.',
  },
  'kookaburra-rake': {
    key: 'kookaburra-rake', name: 'Kookaburra Rake', brand: 'Kookaburra Tools', kind: 'rake', priceAud: 59,
    power: 'none', tier: 1,
    blurb: 'Never runs out of fuel. You might.',
  },
  'kookaburra-tarp': {
    key: 'kookaburra-tarp', name: 'Kookaburra Tarp', brand: 'Kookaburra Tools', kind: 'tarp', priceAud: 89,
    power: 'none', tier: 1,
    blurb: 'Blue, obviously. Rake it on, fold it up, bag it.',
  },
};

export const STARTER_MOWER = 'redgum-46';
export const START_CASH_AUD = 3500;
export const FUEL_AUD_PER_L = 2.1;
export const SERVICE_AUD = 45;
export const SHARPEN_AUD = 25;
export const RESALE_FRACTION = 0.7;
export const TRAILER = { priceAud: 2400, baseSlots: 3, upgradedSlots: 5, tier: 2 } as const;
/** Rep points at which levels 1..6 begin. */
export const REP_LEVELS = [0, 60, 150, 300, 500, 800] as const;
export const HISTORY_KEEP = 50;

// ─── Feature P7: battery vs petrol ──────────────────────────────────────
/** Recharge in the service bay: flat rate, any charge level. */
export const RECHARGE_AUD = 1;
/**
 * Trigger seconds per full charge. A full-service Kerbside backyard is ~57 s
 * of trimming at par (37 s for the autopilot), so a charge covers one such job
 * at a relaxed pace, and not two (DECISIONS.md).
 */
export const BATTERY_RUN_S = 120;
/** The HUD warns at or below this charge %. */
export const LOW_CHARGE_PCT = 15;
/** Powered hand tools: engine condition lost per minute of trigger time (the crew's rate). */
export const TOOL_WEAR_PER_MIN = 0.004;
/** Battery tools wear this much faster than petrol. */
export const BATTERY_WEAR_X = 1.6;
/** HUD copy at LOW_CHARGE_PCT, and when the tool dies. */
export const LOW_CHARGE_COPY = 'Flat as a tack — charge it at the bay.';
export const FLAT_COPY = 'Battery’s dead. Charge it at the bay.';

/** Does this model run on charge (battery hand tools; no battery mower exists)? */
export function usesCharge(m: EquipmentModel | undefined): boolean {
  return !!m && m.power === 'battery' && m.kind !== 'mower';
}

/** Trigger seconds on a full charge: the model's pack (`runS`), else BATTERY_RUN_S. */
export function runSecondsOf(m: Pick<EquipmentModel, 'runS'>): number {
  const r = m.runS;
  return typeof r === 'number' && Number.isFinite(r) && r > 0 ? r : BATTERY_RUN_S;
}

/** Charge % of an owned item: absent (old saves) reads as full. */
export function chargeOf(item: Pick<OwnedEquipment, 'chargePct'>): number {
  const c = item.chargePct;
  return typeof c === 'number' && Number.isFinite(c) ? Math.min(100, Math.max(0, c)) : 100;
}

/** Engine wear per minute of trigger time for a hand tool (battery ×1.6, none for hand gear). */
export function toolWearPerMin(m: EquipmentModel): number {
  if (m.power === 'none') return 0;
  return m.power === 'battery' ? TOOL_WEAR_PER_MIN * BATTERY_WEAR_X : TOOL_WEAR_PER_MIN;
}

export interface SaveGame {
  version: 1;
  companyName: string;
  cash: number;
  repPoints: number;
  owned: OwnedEquipment[];
  trailer: boolean;
  history: JobResult[];
  /** Counter for owned-item uids. */
  nextUid: number;
  jobsDone: number;
  /** Feature P4: the crew. Absent on saves from before it (staff.ts migrateStaff). */
  staff?: StaffState;
  /** Feature P8: yards worked, per layout key (revisits.ts). Absent on saves from before it. */
  visits?: VisitRecord[];
}

export interface TxResult {
  ok: boolean;
  save: SaveGame;
  reason?: string;
  /** Money moved: positive = spent, negative = received. */
  costAud: number;
}

/** Round to cents. */
export function money(x: number): number {
  return Math.round(x * 100) / 100;
}

function clone(s: SaveGame): SaveGame {
  return {
    ...s,
    owned: s.owned.map((o) => ({ ...o })),
    history: s.history.slice(),
  };
}

/** Rep level 1..6 for a point total. */
export function repLevel(points: number): number {
  let lvl = 1;
  for (let i = 0; i < REP_LEVELS.length; i++) if (points >= REP_LEVELS[i]) lvl = i + 1;
  return lvl;
}

/** Points at the current level's floor and the next level's (null at max). */
export function repProgress(points: number): { level: number; floor: number; next: number | null } {
  const level = repLevel(points);
  return { level, floor: REP_LEVELS[level - 1], next: level < REP_LEVELS.length ? REP_LEVELS[level] : null };
}

export function carrySlots(save: SaveGame): number {
  return save.trailer ? TRAILER.upgradedSlots : TRAILER.baseSlots;
}

function newItem(save: SaveGame, modelKey: string): OwnedEquipment {
  const m = MODELS[modelKey];
  return { uid: `eq-${save.nextUid}`, modelKey, engineCondition: 1, bladeCondition: 1, fuelL: m?.tankL ?? 0 };
}

export function newGame(companyName: string): SaveGame {
  const s: SaveGame = {
    version: 1, companyName, cash: START_CASH_AUD, repPoints: 0, owned: [], trailer: false, history: [], nextUid: 1, jobsDone: 0,
  };
  s.owned.push(newItem(s, STARTER_MOWER));
  s.nextUid++;
  return s;
}

/** 70% × price × mean(engine, blade) condition, to the cent. */
export function resaleValue(item: OwnedEquipment, models: Record<string, EquipmentModel> = MODELS): number {
  const m = models[item.modelKey];
  if (!m) return 0;
  return money(RESALE_FRACTION * m.priceAud * ((item.engineCondition + item.bladeCondition) / 2));
}

export function ownedOfKind(save: SaveGame, kind: ToolKind, models: Record<string, EquipmentModel> = MODELS): OwnedEquipment[] {
  return save.owned.filter((o) => models[o.modelKey]?.kind === kind);
}

const refuse = (save: SaveGame, reason: string): TxResult => ({ ok: false, save, reason, costAud: 0 });

// ─── Feature P9: the daily sale tag ─────────────────────────────────────

export const SALE_MIN_PCT = 10;
export const SALE_MAX_PCT = 20;
const SALE_SALT = 0x5a1e;

/** One catalogue item SALE_MIN_PCT–SALE_MAX_PCT% off for a day (local day number). */
export interface DailySale {
  day: number;
  modelKey: string;
  pct: number;
}

/** Today's sale, seeded by the day number: the same all day, a new one tomorrow. */
export function saleForDay(day: number, models: Record<string, EquipmentModel> = MODELS): DailySale | null {
  const keys = Object.values(models).filter((m) => m.priceAud > 0).map((m) => m.key).sort();
  if (!keys.length) return null;
  const rng = mulberry32(mixSeed(Math.floor(day) >>> 0, SALE_SALT));
  const modelKey = keys[Math.floor(rng() * keys.length)];
  const pct = SALE_MIN_PCT + Math.floor(rng() * (SALE_MAX_PCT - SALE_MIN_PCT + 1));
  return { day: Math.floor(day), modelKey, pct };
}

/** What the model costs today: whole dollars off when it's on sale, the list price otherwise. */
export function priceWithSale(m: Pick<EquipmentModel, 'key' | 'priceAud'>, sale?: DailySale | null): number {
  if (!sale || sale.modelKey !== m.key) return m.priceAud;
  return Math.round(m.priceAud * (1 - sale.pct / 100));
}

/** Buy at list price, or at today's sale price when `sale` (Feature P9) names this model. */
export function buy(save: SaveGame, modelKey: string, sale?: DailySale | null): TxResult {
  const m = MODELS[modelKey];
  if (!m) return refuse(save, 'Not in the catalogue.');
  if (repLevel(save.repPoints) < m.tier) return refuse(save, `Unlocks at rep level ${m.tier}.`);
  const price = priceWithSale(m, sale);
  if (save.cash < price) return refuse(save, 'Not enough in the account.');
  const s = clone(save);
  s.cash = money(s.cash - price);
  s.owned.push(newItem(s, modelKey));
  s.nextUid++;
  return { ok: true, save: s, costAud: price };
}

export function sell(save: SaveGame, uid: string): TxResult {
  const item = save.owned.find((o) => o.uid === uid);
  if (!item) return refuse(save, "You don't own that.");
  const m = MODELS[item.modelKey];
  if (m?.kind === 'mower' && ownedOfKind(save, 'mower').length <= 1) return refuse(save, "Can't sell your only mower.");
  const value = resaleValue(item);
  const s = clone(save);
  s.owned = s.owned.filter((o) => o.uid !== uid);
  s.cash = money(s.cash + value);
  return { ok: true, save: s, costAud: -value };
}

/** Fill the tank; if cash is short, buys what it can afford. */
export function refuel(save: SaveGame, uid: string): TxResult {
  const item = save.owned.find((o) => o.uid === uid);
  const m = item && MODELS[item.modelKey];
  if (!item || !m) return refuse(save, "You don't own that.");
  if (m.power !== 'petrol' || !m.tankL) return refuse(save, 'Runs on battery or elbow grease.');
  const want = Math.max(0, m.tankL - item.fuelL);
  if (want <= 1e-6) return refuse(save, 'Tank is already full.');
  const litres = Math.min(want, save.cash / FUEL_AUD_PER_L);
  if (litres <= 1e-6) return refuse(save, 'Not enough in the account.');
  const cost = money(litres * FUEL_AUD_PER_L);
  const s = clone(save);
  const it = s.owned.find((o) => o.uid === uid)!;
  it.fuelL = Math.min(m.tankL, it.fuelL + litres);
  s.cash = money(Math.max(0, s.cash - cost));
  return { ok: true, save: s, costAud: cost };
}

/** Feature P7: charge a battery tool to 100% for RECHARGE_AUD. */
export function recharge(save: SaveGame, uid: string, models: Record<string, EquipmentModel> = MODELS): TxResult {
  const item = save.owned.find((o) => o.uid === uid);
  const m = item && models[item.modelKey];
  if (!item || !m) return refuse(save, "You don't own that.");
  if (!usesCharge(m)) return refuse(save, 'No battery in that.');
  if (chargeOf(item) >= 100) return refuse(save, 'Already charged.');
  if (save.cash < RECHARGE_AUD) return refuse(save, 'Not enough in the account.');
  const s = clone(save);
  s.owned.find((o) => o.uid === uid)!.chargePct = 100;
  s.cash = money(s.cash - RECHARGE_AUD);
  return { ok: true, save: s, costAud: RECHARGE_AUD };
}

/**
 * Feature P7: old saves have no `chargePct` on battery tools; give them a full
 * charge (and clamp a stored one to 0–100). Other gear is left exactly as is.
 */
export function migrateCharge(save: SaveGame, models: Record<string, EquipmentModel> = MODELS): SaveGame {
  if (!save.owned.some((o) => usesCharge(models[o.modelKey]) && o.chargePct !== chargeOf(o))) return save;
  const s = clone(save);
  for (const o of s.owned) if (usesCharge(models[o.modelKey])) o.chargePct = chargeOf(o);
  return s;
}

export function service(save: SaveGame, uid: string): TxResult {
  const item = save.owned.find((o) => o.uid === uid);
  const m = item && MODELS[item.modelKey];
  if (!item || !m) return refuse(save, "You don't own that.");
  if (m.power === 'none') return refuse(save, 'Nothing to service.');
  if (item.engineCondition >= 1) return refuse(save, 'Engine is already sweet.');
  if (save.cash < SERVICE_AUD) return refuse(save, 'Not enough in the account.');
  const s = clone(save);
  s.owned.find((o) => o.uid === uid)!.engineCondition = 1;
  s.cash = money(s.cash - SERVICE_AUD);
  return { ok: true, save: s, costAud: SERVICE_AUD };
}

export function sharpen(save: SaveGame, uid: string): TxResult {
  const item = save.owned.find((o) => o.uid === uid);
  const m = item && MODELS[item.modelKey];
  if (!item || !m) return refuse(save, "You don't own that.");
  if (m.kind !== 'mower') return refuse(save, 'No blades on that.');
  if (item.bladeCondition >= 1) return refuse(save, 'Blades are already sharp.');
  if (save.cash < SHARPEN_AUD) return refuse(save, 'Not enough in the account.');
  const s = clone(save);
  s.owned.find((o) => o.uid === uid)!.bladeCondition = 1;
  s.cash = money(s.cash - SHARPEN_AUD);
  return { ok: true, save: s, costAud: SHARPEN_AUD };
}

export function buyTrailer(save: SaveGame): TxResult {
  if (save.trailer) return refuse(save, 'Already got the big trailer.');
  if (repLevel(save.repPoints) < TRAILER.tier) return refuse(save, `Unlocks at rep level ${TRAILER.tier}.`);
  if (save.cash < TRAILER.priceAud) return refuse(save, 'Not enough in the account.');
  const s = clone(save);
  s.trailer = true;
  s.cash = money(s.cash - TRAILER.priceAud);
  return { ok: true, save: s, costAud: TRAILER.priceAud };
}

/** Bank a finished (or abandoned) job: cash, rep, history, gear wear and fuel. */
export function applyJobResult(save: SaveGame, result: JobResult): SaveGame {
  const s = clone(save);
  s.cash = money(s.cash + result.totalAud);
  s.repPoints += result.repPoints;
  s.jobsDone += 1;
  const { gear: _gear, ...plain } = result;
  s.history.push(plain);
  if (s.history.length > HISTORY_KEEP) s.history = s.history.slice(-HISTORY_KEEP);
  for (const g of result.gear ?? []) {
    const it = s.owned.find((o) => o.uid === g.uid);
    if (!it) continue;
    it.engineCondition = clamp01(g.engineCondition);
    it.bladeCondition = clamp01(g.bladeCondition);
    it.fuelL = Math.max(0, g.fuelL);
    // Feature P7: only a battery job brings a charge home.
    if (g.chargePct !== undefined) it.chargePct = chargeOf(g);
  }
  return s;
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
